/**
 * OnboardingContext
 *
 * Manages the full lifecycle of the first-time admin onboarding wizard.
 *
 * State model (dual-write: localStorage cache + backend API):
 *  - hasDismissed   React state only — suppresses auto-show for the rest of the session.
 *                   Resets on refresh. Only 1 skip is granted; after that skipCount hits max
 *                   and the wizard will never auto-show again.
 *  - hasCompleted   Persisted to backend + localStorage cache.
 *                   Hides everything permanently once the user clicks "Finish Setup".
 *  - savedStep      Persisted to backend + localStorage cache.
 *                   Wizard reopens at the correct step after a refresh.
 *  - skipCount      Persisted to backend + localStorage cache. Max 1 skip.
 *
 * On mount the context reads from localStorage for instant display, then fetches
 * from the backend. If the backend has newer state, it wins and localStorage is updated.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { apiFetch } from '@/lib/api-error-handler';
import { getApiBaseUrl } from '@/lib/data-service';

export interface OnboardingContextValue {
  // State
  hasDismissed: boolean;
  hasCompleted: boolean;
  isWizardOpen: boolean;
  savedStep: number;
  skipCount: number;
  isHydrating: boolean;

  // Derived
  shouldAutoShow: boolean;

  // Actions
  openWizard: () => void;
  closeWizard: () => void;
  dismissOnboarding: () => void;
  completeOnboarding: (stepStatuses?: Record<number, string>) => void;
  resetOnboarding: () => void;
  saveStep: (step: number) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

const MAX_SKIPS = 1;

function storageKey(suffix: string, userId: string) {
  return `onboarding_${suffix}_${userId}`;
}

function readLocalStorage(suffix: string, uid: string): string | null {
  try {
    return localStorage.getItem(storageKey(suffix, uid));
  } catch {
    return null;
  }
}

function writeLocalStorage(suffix: string, uid: string, value: string) {
  try {
    localStorage.setItem(storageKey(suffix, uid), value);
  } catch {
    // ignore
  }
}

function removeLocalStorage(suffix: string, uid: string) {
  try {
    localStorage.removeItem(storageKey(suffix, uid));
  } catch {
    // ignore
  }
}

// Fire-and-forget API call — never blocks the UI
function persistToApi(body: Record<string, unknown>, endpoint = 'status') {
  const url = `${getApiBaseUrl()}/onboarding/${endpoint}`;
  const method = endpoint === 'complete' ? 'POST' : 'PUT';
  apiFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch((err) => {
    console.warn('[OnboardingContext] Backend persist failed — localStorage is still up to date:', err);
  });
}

interface OnboardingProviderProps {
  children: ReactNode;
  userId: string | null;
}

export function OnboardingProvider({ children, userId }: OnboardingProviderProps) {
  const uid = userId ?? '__anonymous__';

  // ── Persisted state (initialised from localStorage for zero-flicker) ────
  const [hasCompleted, setHasCompleted] = useState<boolean>(
    () => readLocalStorage('completed', uid) === 'true',
  );

  const [savedStep, setSavedStepState] = useState<number>(() => {
    const raw = readLocalStorage('step', uid);
    return raw ? parseInt(raw, 10) : 0;
  });

  const [skipCount, setSkipCount] = useState<number>(() => {
    const raw = readLocalStorage('skipCount', uid);
    return raw ? parseInt(raw, 10) : 0;
  });

  // ── Session-only state ───────────────────────────────────────────────────
  const [hasDismissed, setHasDismissed] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isHydrating, setIsHydrating] = useState(!!userId);

  const hasFetchedFromApi = useRef(false);

  // ── Hydrate from backend on mount (backend wins over localStorage) ──────
  useEffect(() => {
    if (!userId) {
      setIsHydrating(false);
      return;
    }
    if (hasFetchedFromApi.current) return;
    hasFetchedFromApi.current = true;

    apiFetch<{
      hasCompleted: boolean;
      savedStep: number;
      skipCount: number;
    }>(`${getApiBaseUrl()}/onboarding/status`)
      .then((data) => {
        if (!data) return;
        setHasCompleted(data.hasCompleted);
        setSavedStepState(data.savedStep);
        setSkipCount(data.skipCount);
        // Sync localStorage cache
        writeLocalStorage('completed', uid, String(data.hasCompleted));
        writeLocalStorage('step', uid, String(data.savedStep));
        writeLocalStorage('skipCount', uid, String(data.skipCount));
      })
      .catch((err) => {
        console.warn('[OnboardingContext] Backend fetch failed — using localStorage cache:', err);
      })
      .finally(() => {
        setIsHydrating(false);
      });
  }, [uid, userId]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const shouldAutoShow = !isHydrating && !hasCompleted && !hasDismissed && skipCount < MAX_SKIPS;

  // ── Actions ──────────────────────────────────────────────────────────────
  const openWizard = useCallback(() => {
    setIsWizardOpen(true);
  }, []);

  const closeWizard = useCallback(() => {
    setIsWizardOpen(false);
  }, []);

  const dismissOnboarding = useCallback(() => {
    const newCount = skipCount + 1;
    setSkipCount(newCount);
    writeLocalStorage('skipCount', uid, String(newCount));
    persistToApi({ skipCount: newCount });
    setHasDismissed(true);
    setIsWizardOpen(false);
  }, [skipCount, uid]);

  const completeOnboarding = useCallback(
    (stepStatuses?: Record<number, string>) => {
      setHasCompleted(true);
      writeLocalStorage('completed', uid, 'true');
      persistToApi({ stepStatuses: stepStatuses ?? {} }, 'complete');
      setIsWizardOpen(false);
    },
    [uid],
  );

  const resetOnboarding = useCallback(() => {
    setHasCompleted(false);
    setHasDismissed(false);
    setSavedStepState(0);
    setSkipCount(0);
    setIsWizardOpen(true);
    removeLocalStorage('completed', uid);
    removeLocalStorage('step', uid);
    removeLocalStorage('skipCount', uid);
    persistToApi({ hasCompleted: false, savedStep: 0, skipCount: 0 });
  }, [uid]);

  const saveStep = useCallback(
    (step: number) => {
      setSavedStepState(step);
      writeLocalStorage('step', uid, String(step));
      persistToApi({ savedStep: step });
    },
    [uid],
  );

  const value: OnboardingContextValue = {
    hasDismissed,
    hasCompleted,
    isWizardOpen,
    savedStep,
    skipCount,
    isHydrating,
    shouldAutoShow,
    openWizard,
    closeWizard,
    dismissOnboarding,
    completeOnboarding,
    resetOnboarding,
    saveStep,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
}
